import React, { useEffect, useMemo, useState } from "react";
import { useKeycloak } from "@react-keycloak/web";
import uuid from "react-uuid";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import TabbedContainer from "../../../App/components/common/TabbedContainer";
import Loading from "../../../common/components/loading/loading";
import { getOrganizations } from "../../../common/services/auth/organizationService";
import { getUserAccountsByOrganization } from "../../../common/services/auth/userAccountOrganizationService";
import { getMarketControl } from "../../../common/services/tradingEngine/marketStateService";
import { getMarketsWithAttributes } from "../../../common/services/markets/marketService";
import {
  getDelegatedOrganizationOrderReleaseLimits,
  getDelegatedUserAccountOrderReleaseLimits,
  getOrganizationOrderReleaseLimits,
  getUserAccountOrderReleaseLimits,
  saveDelegatedOrganizationOrderReleaseLimits,
  saveDelegatedUserAccountOrderReleaseLimits,
  saveOrganizationOrderReleaseLimits,
  saveUserAccountOrderReleaseLimits,
} from "../../../common/services/tradingEngine/orderReleaseLimitService";

const getLimitKey = (marketId, priceBasisId) => `${marketId}:${priceBasisId}`;

const parseNumberInput = (value) => {
  if (value === "" || value === null || value === undefined) {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const hasAnyLimitValue = (limit) =>
  [
    limit.maxVolume,
    limit.maxTotalValue,
    limit.priceVariancePercentage,
    limit.priceVarianceAbsolute,
  ].some((value) => value !== null && value !== undefined && value !== "");

const toLimitMap = (limits) =>
  (limits || []).reduce((accumulator, limit) => {
    const key = getLimitKey(limit.marketId, limit.priceBasisId);
    accumulator[key] = {
      key,
      marketId: limit.marketId,
      priceBasisId: limit.priceBasisId,
      maxVolume: limit.maxVolume ?? null,
      maxTotalValue: limit.maxTotalValue ?? null,
      priceVariancePercentage: limit.priceVariancePercentage ?? null,
      priceVarianceAbsolute: limit.priceVarianceAbsolute ?? null,
    };
    return accumulator;
  }, {});

const buildDraftRows = (markets, limitMap) =>
  (markets || []).flatMap((market) =>
    (market.marketAttributes || [])
      .filter((marketAttribute) => marketAttribute.active !== false)
      .map((marketAttribute) => {
        const key = getLimitKey(market.marketId, marketAttribute.priceBasisId);
        const configuredLimit = limitMap[key] || {};
        return {
          key,
          marketId: market.marketId,
          marketName: market.name,
          priceBasisId: marketAttribute.priceBasisId,
          priceBasisName: marketAttribute.priceBasisName,
          currencyName: marketAttribute.currencyName,
          uomName: marketAttribute.uomName,
          maxVolume: configuredLimit.maxVolume ?? null,
          maxTotalValue: configuredLimit.maxTotalValue ?? null,
          priceVariancePercentage:
            configuredLimit.priceVariancePercentage ?? null,
          priceVarianceAbsolute:
            configuredLimit.priceVarianceAbsolute ?? null,
        };
      })
  );

const buildRequestPayload = (organizationId, userAccountId, rows) =>
  (rows || [])
    .filter(hasAnyLimitValue)
    .map((row) => ({
      organizationId,
      userAccountId,
      marketId: row.marketId,
      priceBasisId: row.priceBasisId,
      maxVolume: row.maxVolume,
      maxTotalValue: row.maxTotalValue,
      priceVariancePercentage: row.priceVariancePercentage,
      priceVarianceAbsolute: row.priceVarianceAbsolute,
      active: true,
      eventId: uuid(),
    }));

const OrderReleaseLimitTable = ({ rows, allowEdit, onChange, t }) => {
  return (
    <div className="table-responsive">
      <table className="table table-sm table-striped align-middle mb-0">
        <thead>
          <tr>
            <th>{t("Market")}</th>
            <th>{t("PriceBasis")}</th>
            <th>{t("Currency")}</th>
            <th>{t("Uom")}</th>
            <th>{t("VolumeLimit")}</th>
            <th>{t("TotalValueLimit")}</th>
            <th>{t("PriceVariancePercentageLimit")}</th>
            <th>{t("PriceVarianceAbsoluteLimit")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <td>{row.marketName}</td>
              <td>{row.priceBasisName}</td>
              <td>{row.currencyName}</td>
              <td>{row.uomName}</td>
              <td>
                <input
                  className="form-control form-control-sm"
                  type="number"
                  min="0"
                  step="any"
                  disabled={!allowEdit}
                  value={row.maxVolume ?? ""}
                  onChange={(event) =>
                    onChange(row.key, row.marketId, row.priceBasisId, "maxVolume", event.currentTarget.value)
                  }
                />
              </td>
              <td>
                <input
                  className="form-control form-control-sm"
                  type="number"
                  min="0"
                  step="any"
                  disabled={!allowEdit}
                  value={row.maxTotalValue ?? ""}
                  onChange={(event) =>
                    onChange(
                      row.key,
                      row.marketId,
                      row.priceBasisId,
                      "maxTotalValue",
                      event.currentTarget.value,
                    )
                  }
                />
              </td>
              <td>
                <input
                  className="form-control form-control-sm"
                  type="number"
                  min="0"
                  step="any"
                  disabled={!allowEdit}
                  value={row.priceVariancePercentage ?? ""}
                  onChange={(event) =>
                    onChange(
                      row.key,
                      row.marketId,
                      row.priceBasisId,
                      "priceVariancePercentage",
                      event.currentTarget.value,
                    )
                  }
                />
              </td>
              <td>
                <input
                  className="form-control form-control-sm"
                  type="number"
                  min="0"
                  step="any"
                  disabled={!allowEdit}
                  value={row.priceVarianceAbsolute ?? ""}
                  onChange={(event) =>
                    onChange(
                      row.key,
                      row.marketId,
                      row.priceBasisId,
                      "priceVarianceAbsolute",
                      event.currentTarget.value,
                    )
                  }
                />
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={8} className="text-muted text-center">
                {t("NoMarketsAvailableForReleaseLimits")}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

const OrderReleaseLimitManager = ({
  delegated = false,
  allowEdit = false,
  restrictToAssociatedOrganizations = delegated,
}) => {
  const { t } = useTranslation(["trading", "common"]);
  const { keycloak } = useKeycloak();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("organization");
  const [organizations, setOrganizations] = useState([]);
  const [marketsByOrganizationId, setMarketsByOrganizationId] = useState({});
  const [selectedOrganizationId, setSelectedOrganizationId] = useState("");
  const [selectedUserAccountId, setSelectedUserAccountId] = useState("");
  const [userAccounts, setUserAccounts] = useState([]);
  const [organizationLimitMap, setOrganizationLimitMap] = useState({});
  const [userAccountLimitMap, setUserAccountLimitMap] = useState({});

  const delegatedOrganizationIds = useMemo(() => {
    const rawGroups = [
      keycloak?.tokenParsed?.["http://schemas.xmlsoap.org/claims/Group"],
      keycloak?.tokenParsed?.groups,
      keycloak?.tokenParsed?.group,
    ].flatMap((value) => {
      if (Array.isArray(value)) {
        return value;
      }
      return value ? [value] : [];
    });

    return Array.from(new Set(rawGroups)).filter((value) =>
      /^[0-9a-fA-F-]{36}$/.test(value),
    );
  }, [keycloak]);

  const selectedMarkets = useMemo(
    () => marketsByOrganizationId[selectedOrganizationId] || [],
    [marketsByOrganizationId, selectedOrganizationId],
  );

  const organizationRows = useMemo(
    () => buildDraftRows(selectedMarkets, organizationLimitMap),
    [selectedMarkets, organizationLimitMap],
  );

  const userAccountRows = useMemo(
    () => buildDraftRows(selectedMarkets, userAccountLimitMap),
    [selectedMarkets, userAccountLimitMap],
  );

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const [allOrganizations, allMarkets, marketControl] = await Promise.all([
          getOrganizations(),
          getMarketsWithAttributes(),
          getMarketControl(),
        ]);

        const marketLookup = new Map(
          (allMarkets || [])
            .filter((market) => market.active)
            .map((market) => [market.marketId, market]),
        );

        const nextMarketsByOrganizationId = {};
        (marketControl || [])
          .filter((market) => market.active)
          .forEach((market) => {
            const marketDetails = marketLookup.get(market.marketId);
            if (!marketDetails) {
              return;
            }

            (market.organizationIds || []).forEach((organizationId) => {
              nextMarketsByOrganizationId[organizationId] = [
                ...(nextMarketsByOrganizationId[organizationId] || []),
                marketDetails,
              ]
                .filter(
                  (candidate, index, candidates) =>
                    candidates.findIndex(
                      (item) => item.marketId === candidate.marketId,
                    ) === index,
                )
                .sort((left, right) => left.name.localeCompare(right.name));
            });
          });

        const nextOrganizations = (allOrganizations || [])
          .filter((organization) => organization.active)
          .filter((organization) => nextMarketsByOrganizationId[organization.organizationId])
          .filter((organization) =>
            !restrictToAssociatedOrganizations ||
            delegatedOrganizationIds.includes(organization.organizationId),
          )
          .sort((left, right) => left.name.localeCompare(right.name));

        setOrganizations(nextOrganizations);
        setMarketsByOrganizationId(nextMarketsByOrganizationId);
        setSelectedOrganizationId(nextOrganizations[0]?.organizationId || "");
      } catch (error) {
        console.error("Failed to load order release limit manager data", error);
        toast.error(t("OrderReleaseLimitLoadFailed"));
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [delegatedOrganizationIds, restrictToAssociatedOrganizations, t]);

  useEffect(() => {
    if (!selectedOrganizationId) {
      setOrganizationLimitMap({});
      setUserAccounts([]);
      setSelectedUserAccountId("");
      return;
    }

    const loadOrganizationLimits = async () => {
      try {
        const limits = delegated
          ? await getDelegatedOrganizationOrderReleaseLimits(selectedOrganizationId)
          : await getOrganizationOrderReleaseLimits(selectedOrganizationId);
        setOrganizationLimitMap(toLimitMap(limits));
      } catch (error) {
        console.error("Failed to load organization order release limits", error);
        toast.error(t("OrderReleaseLimitLoadFailed"));
      }
    };

    const loadUsers = async () => {
      try {
        const nextUsers = (await getUserAccountsByOrganization(selectedOrganizationId)) || [];
        setUserAccounts(nextUsers);
        setSelectedUserAccountId(nextUsers[0]?.userAccountId || "");
      } catch (error) {
        console.error("Failed to load organization users for release limits", error);
        setUserAccounts([]);
        setSelectedUserAccountId("");
      }
    };

    loadOrganizationLimits();
    loadUsers();
  }, [delegated, selectedOrganizationId, t]);

  useEffect(() => {
    if (!selectedOrganizationId || !selectedUserAccountId) {
      setUserAccountLimitMap({});
      return;
    }

    const loadUserLimits = async () => {
      try {
        const limits = delegated
          ? await getDelegatedUserAccountOrderReleaseLimits(
              selectedOrganizationId,
              selectedUserAccountId,
            )
          : await getUserAccountOrderReleaseLimits(
              selectedOrganizationId,
              selectedUserAccountId,
            );
        setUserAccountLimitMap(toLimitMap(limits));
      } catch (error) {
        console.error("Failed to load user order release limits", error);
        toast.error(t("OrderReleaseLimitLoadFailed"));
      }
    };

    loadUserLimits();
  }, [delegated, selectedOrganizationId, selectedUserAccountId, t]);

  const updateOrganizationLimit = (key, marketId, priceBasisId, field, value) => {
    setOrganizationLimitMap((current) => ({
      ...current,
      [key]: {
        key,
        marketId,
        priceBasisId,
        ...(current[key] || {}),
        [field]: parseNumberInput(value),
      },
    }));
  };

  const updateUserAccountLimit = (key, marketId, priceBasisId, field, value) => {
    setUserAccountLimitMap((current) => ({
      ...current,
      [key]: {
        key,
        marketId,
        priceBasisId,
        ...(current[key] || {}),
        [field]: parseNumberInput(value),
      },
    }));
  };

  const saveOrganizationLimits = async () => {
    if (!selectedOrganizationId) {
      return;
    }

    setIsSaving(true);
    try {
      const payload = buildRequestPayload(
        selectedOrganizationId,
        null,
        organizationRows,
      ).map(({ userAccountId, ...limit }) => limit);

      const savedLimits = delegated
        ? await saveDelegatedOrganizationOrderReleaseLimits(
            selectedOrganizationId,
            payload,
          )
        : await saveOrganizationOrderReleaseLimits(selectedOrganizationId, payload);

      setOrganizationLimitMap(toLimitMap(savedLimits));
      toast.success(t("OrderReleaseLimitSaved"));
    } catch (error) {
      console.error("Failed to save organization order release limits", error);
      toast.error(t("OrderReleaseLimitSaveFailed"));
    } finally {
      setIsSaving(false);
    }
  };

  const saveUserLimits = async () => {
    if (!selectedOrganizationId || !selectedUserAccountId) {
      return;
    }

    setIsSaving(true);
    try {
      const payload = buildRequestPayload(
        selectedOrganizationId,
        selectedUserAccountId,
        userAccountRows,
      );

      const savedLimits = delegated
        ? await saveDelegatedUserAccountOrderReleaseLimits(
            selectedOrganizationId,
            selectedUserAccountId,
            payload,
          )
        : await saveUserAccountOrderReleaseLimits(
            selectedOrganizationId,
            selectedUserAccountId,
            payload,
          );

      setUserAccountLimitMap(toLimitMap(savedLimits));
      toast.success(t("OrderReleaseLimitSaved"));
    } catch (error) {
      console.error("Failed to save user order release limits", error);
      toast.error(t("OrderReleaseLimitSaveFailed"));
    } finally {
      setIsSaving(false);
    }
  };

  const tabs = [
    {
      id: "organization",
      label: t("OrganizationLimits"),
      content: (
        <div className="card border-0 shadow-sm">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <p className="text-muted mb-0">
                {t("OrderReleaseLimitOrganizationHelpText")}
              </p>
              <button
                type="button"
                className="btn btn-sm btn-success"
                disabled={!allowEdit || isSaving || !selectedOrganizationId}
                onClick={saveOrganizationLimits}
              >
                {t("Save")}
              </button>
            </div>
            <OrderReleaseLimitTable
              rows={organizationRows}
              allowEdit={allowEdit}
              onChange={updateOrganizationLimit}
              t={t}
            />
          </div>
        </div>
      ),
    },
    {
      id: "user",
      label: t("UserLimits"),
      content: (
        <div className="card border-0 shadow-sm">
          <div className="card-body">
            <div className="row g-3 align-items-end mb-3">
              <div className="col-md-6">
                <label className="form-label">{t("UserAccount")}</label>
                <select
                  className="form-select form-select-sm"
                  value={selectedUserAccountId}
                  onChange={(event) =>
                    setSelectedUserAccountId(event.currentTarget.value)
                  }
                >
                  {userAccounts.map((userAccount) => (
                    <option
                      key={userAccount.userAccountId}
                      value={userAccount.userAccountId}
                    >
                      {userAccount.login}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-auto ms-md-auto">
                <button
                  type="button"
                  className="btn btn-sm btn-success"
                  disabled={
                    !allowEdit ||
                    isSaving ||
                    !selectedOrganizationId ||
                    !selectedUserAccountId
                  }
                  onClick={saveUserLimits}
                >
                  {t("Save")}
                </button>
              </div>
            </div>
            <p className="text-muted">
              {t("OrderReleaseLimitUserHelpText")}
            </p>
            <OrderReleaseLimitTable
              rows={userAccountRows}
              allowEdit={allowEdit}
              onChange={updateUserAccountLimit}
              t={t}
            />
          </div>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="container-fluid">
      <div className="row g-3 mb-3 align-items-end">
        <div className="col-md-6">
          <label className="form-label">{t("Organization")}</label>
          <select
            className="form-select form-select-sm"
            value={selectedOrganizationId}
            onChange={(event) => setSelectedOrganizationId(event.currentTarget.value)}
          >
            {organizations.map((organization) => (
              <option
                key={organization.organizationId}
                value={organization.organizationId}
              >
                {organization.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <TabbedContainer
        activeTab={activeTab}
        onTabChange={setActiveTab}
        tabs={tabs}
      />
    </div>
  );
};

export default OrderReleaseLimitManager;
